import { CalendarDays } from 'lucide-react';
import { WaitingOn, type IsoDate } from '@clawhire/shared';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { EnumBadge } from '@/components/data/EnumBadge';
import { RateText } from '@/components/data/RateText';
import { candidateLine, formatPct1, overdueDays } from '@/pages/submissions/helpers';
import type { SubmissionListItem } from '@/pages/submissions/types';

/** Card on the "By status" board. */
export function SubmissionCard({ item, today }: { item: SubmissionListItem; today: IsoDate }) {
  const overdue = item.computed.isTerminal ? null : overdueDays(item.followUpOn, today, true);
  const actual = item.rates.actualMarginPct;
  const below = actual !== null && actual < item.rates.effectiveMargin.marginPct - 1e-9;
  return (
    <div className="space-y-1.5">
      <div className="font-medium leading-snug">{item.computed.title}</div>
      <div className="text-xs text-muted-foreground">{candidateLine(item.candidate)}</div>
      {item.waitingOn || item.computed.needsChase ? (
        <div className="flex flex-wrap items-center gap-1">
          {item.waitingOn ? <EnumBadge def={WaitingOn} value={item.waitingOn} size="sm" /> : null}
          {item.computed.needsChase ? (
            <Badge variant="destructive" className="px-1.5 text-[11px]">
              Chase
            </Badge>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
        <span className="whitespace-nowrap">
          <RateText rate={item.candidateRateEur} unit={item.candidateRateUnit} />
          <span aria-hidden="true"> → </span>
          <RateText rate={item.rates.offerInUse} />
          {actual !== null ? (
            <span className={cn('ml-1', below && 'font-medium text-destructive')}>
              ({formatPct1(actual)})
            </span>
          ) : null}
        </span>
        {item.followUpOn ? (
          <span
            className={cn(
              'inline-flex items-center gap-1',
              overdue !== null && 'font-medium text-destructive',
            )}
          >
            <CalendarDays className="h-3 w-3" aria-hidden="true" />
            <time dateTime={item.followUpOn}>{formatDate(item.followUpOn)}</time>
          </span>
        ) : null}
      </div>
    </div>
  );
}
